<template>
  <v-container fluid class="ps-manage-users-page">
    <!-- Summary Cards (4 Cards) -->
    <KpiCards class="mb-4" :items="kpiItems" :loading="loadingSummary" :skeleton="4" />

    <!-- Main Content Container -->
    <v-card flat class="rounded-xl main-users-card border">
      <!-- Top Title and Action Bar -->
      <div class="pa-4 pa-sm-5 pb-3">
        <div
          class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center"
          style="gap: 16px"
        >
          <div>
            <h2 class="font-weight-bold text-h5 mb-0 page-title">
              {{ $t("staff_management") }}
            </h2>
            <p class="caption grey--text text--darken-1 mb-0 mt-1">
              {{
                $t("all_store_staff") ||
                "Manage staff credentials, assigned branches, and access permissions"
              }}
            </p>
          </div>

          <!-- Top Actions -->
          <div
            class="d-flex align-center flex-wrap"
            style="
              gap: 10px;
              width: 100%;
              width: md-auto;
              justify-content: flex-end;
            "
          >
            <!-- Primary Add Button -->
            <v-btn
              depressed
              color="primary"
              @click="newItem"
              class="white--text btn-primary-brand rounded-lg font-weight-bold px-4"
              :small="$vuetify.breakpoint.xs"
            >
              <v-icon left small>fal fa-plus</v-icon>
              {{ $t("add_staff") }}
            </v-btn>
          </div>
        </div>

        <!-- Filter & Search Controls Bar -->
        <div class="mt-4 pt-3 border-top filter-toolbar">
          <v-row dense align="center">
            <!-- Search Field -->
            <v-col cols="12" md="5" lg="4">
              <v-text-field
                v-model="searchQuery"
                dense
                outlined
                hide-details
                color="primary"
                clearable
                :loading="loadingUsers"
                class="rounded-lg user-search-input"
                :placeholder="
                  ($t('search_staff') || 'Search staff') +
                  ' (name, phone, email)...'
                "
                prepend-inner-icon="fal fa-search"
                @input="onSearchInput"
                @click:clear="onClearSearch"
              ></v-text-field>
            </v-col>

            <!-- Filters: Active/Inactive Chips & Branch Select Only -->
            <v-col cols="12" md="7" lg="8">
              <div
                class="d-flex align-center flex-wrap justify-start justify-md-end"
                style="gap: 10px"
              >
                <!-- Active / Inactive Status Tabs (.br-tabs - Matches /report-branches style) -->
                <div class="br-tabs">
                  <div
                    class="br-tab"
                    :class="{ 'br-tab--active': selectedStatusFilter === 'all' }"
                    @click="selectedStatusFilter = 'all'"
                  >
                    {{ $t("all") || "All" }}
                    <span class="br-tab-count">{{ totalStaffCount }}</span>
                  </div>
                  <div
                    class="br-tab"
                    :class="{ 'br-tab--active': selectedStatusFilter === 1 }"
                    @click="selectedStatusFilter = 1"
                  >
                    <v-icon left x-small color="success" class="mr-1" style="font-size: 8px">fas fa-circle</v-icon>
                    {{ $t("active") || "Active" }}
                    <span class="br-tab-count">{{ activeCount }}</span>
                  </div>
                  <div
                    class="br-tab"
                    :class="{ 'br-tab--active': selectedStatusFilter === 0 }"
                    @click="selectedStatusFilter = 0"
                  >
                    <v-icon left x-small color="error" class="mr-1" style="font-size: 8px">fas fa-circle</v-icon>
                    {{ $t("inactive") || "Inactive" }}
                    <span class="br-tab-count">{{ inactiveCount }}</span>
                  </div>
                </div>

                <!-- Branch Filter Dropdown -->
                <div style="min-width: 160px; max-width: 220px">
                  <v-select
                    v-model="selectedBranchFilter"
                    :items="branchFilterOptions"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="rounded-lg branch-select-field"
                    :placeholder="$t('branch')"
                    prepend-inner-icon="fal fa-store-alt"
                  ></v-select>
                </div>

                <!-- Reset Filters Button -->
                <v-btn
                  v-if="isAnyFilterActive"
                  text
                  small
                  color="grey darken-1"
                  class="px-2 rounded-lg"
                  @click="resetFilters"
                >
                  <v-icon left x-small>fal fa-times-circle</v-icon>
                  {{ $t("clear_filter") || "Clear" }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Content Area -->
      <div class="pa-4 pa-sm-5">
        <!-- Loading Skeleton -->
        <div v-if="loadingUsers">
          <v-card v-for="n in 5" :key="n" outlined class="rounded-xl mb-3 pa-4">
            <v-skeleton-loader type="list-item-avatar-two-line" />
          </v-card>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredUsers.length === 0"
          class="text-center py-10 empty-state-container"
        >
          <div class="empty-state-icon-wrap mx-auto mb-3">
            <v-icon size="48" color="primary">fal fa-user-friends</v-icon>
          </div>
          <h3 class="font-weight-bold text-h6 mb-1">
            {{
              isAnyFilterActive
                ? $t("no_staff_found") || "No staff found"
                : $t("no_staff_yet") || "No staff members yet"
            }}
          </h3>
          <p class="caption grey--text max-w-400 mx-auto mb-4">
            {{
              isAnyFilterActive
                ? "Try adjusting your search terms or filter criteria."
                : "Start assembling your team by adding your first staff member."
            }}
          </p>
          <div class="d-flex justify-center" style="gap: 10px">
            <v-btn
              v-if="isAnyFilterActive"
              outlined
              color="primary"
              small
              class="rounded-lg"
              @click="resetFilters"
            >
              <v-icon left small>fal fa-undo</v-icon>
              {{ $t("clear_filter") || "Reset Filters" }}
            </v-btn>
            <v-btn
              depressed
              color="primary"
              small
              class="rounded-lg btn-primary-brand white--text"
              @click="newItem"
            >
              <v-icon left small>fal fa-plus</v-icon>
              {{ $t("add_staff") }}
            </v-btn>
          </div>
        </div>

        <!-- HORIZONTAL LIST CARDS VIEW -->
        <div v-else>
          <v-card
            v-for="(item, index) in filteredUsers"
            :key="item.id || index"
            outlined
            class="rounded-xl mb-3 staff-row-card"
            :class="{
              'staff-row-card--self': user.id === item.id,
              'staff-row-card--inactive': item.is_active === 0,
            }"
            @click="viewItem(item)"
          >
            <div
              class="staff-row-inner d-flex align-center justify-space-between flex-wrap pa-3 pa-sm-4"
            >
              <!-- Col 1: Avatar + Identity + (YOU) + Staff ID -->
              <div class="staff-col-identity d-flex align-center mr-3 my-1">
                <v-avatar
                  size="48"
                  :color="getColor(index)"
                  class="mr-3 flex-shrink-0 staff-avatar"
                >
                  <app-img
                    v-if="item.avatar"
                    width="48"
                    height="48"
                    :img="item.avatar"
                  />
                  <span v-else class="white--text font-weight-bold subtitle-2">
                    {{ initials(item.first_name, item.last_name) }}
                  </span>
                </v-avatar>

                <div class="staff-identity-meta text-truncate">
                  <div class="d-flex align-center flex-wrap" style="gap: 6px">
                    <span
                      class="font-weight-bold text-subtitle-1 staff-name-link text-truncate"
                      :title="getFullName(item)"
                    >
                      {{ getFullName(item) }}
                    </span>
                    <v-chip
                      v-if="user.id === item.id"
                      x-small
                      color="primary"
                      label
                      class="font-weight-bold px-1 py-0 self-badge"
                    >
                      {{ $t("you") || "YOU" }}
                    </v-chip>
                    <v-chip
                      dark
                      x-small
                      label
                      class="text-capitalize font-weight-medium px-2 role-pill"
                      :color="RoleColor(item.role)"
                    >
                      {{ userRole(item.role) }}
                    </v-chip>
                  </div>

                  <div
                    class="d-flex align-center flex-wrap mt-1"
                    style="gap: 8px"
                  >
                    <span
                      class="user-id-pill"
                      @click.stop="copyText(item.id, 'Staff ID')"
                      title="Copy Staff ID"
                    >
                      #{{ item.id }}
                      <v-icon x-small class="ml-1 grey--text"
                        >fal fa-copy</v-icon
                      >
                    </span>
                    <span class="caption grey--text d-flex align-center">
                      <v-icon x-small class="mr-1">fal fa-calendar-alt</v-icon>
                      {{ item.created_at | moment }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Col 2: Branch Assignment -->
              <div class="staff-col-branch mr-3 my-1">
                <div class="caption grey--text mb-1">{{ $t("branch") }}</div>
                <div>
                  <v-chip
                    x-small
                    outlined
                    label
                    class="font-weight-medium user-branch-chip px-2"
                  >
                    <v-icon x-small left color="primary"
                      >fal fa-store-alt</v-icon
                    >
                    <span class="text-truncate" style="max-width: 130px">
                      {{ getBranchName(item) }}
                    </span>
                  </v-chip>
                </div>
              </div>

              <!-- Col 3: Contact Info (Email & Phone) -->
              <div class="staff-col-contact mr-3 my-1">
                <div class="caption grey--text mb-1">
                  {{ $t("contact_info") }}
                </div>
                <div class="d-flex flex-column" style="gap: 2px">
                  <div class="caption d-flex align-center text-truncate">
                    <v-icon x-small class="mr-1 grey--text"
                      >fal fa-envelope</v-icon
                    >
                    <span
                      class="text-truncate grey--text text--darken-2"
                      style="max-width: 170px"
                      :title="item.email"
                    >
                      {{ item.email || "-" }}
                    </span>
                    <v-btn
                      v-if="item.email"
                      icon
                      x-small
                      class="ml-1 contact-mini-copy"
                      @click.stop="copyText(item.email, 'Email')"
                      title="Copy Email"
                    >
                      <v-icon x-small>fal fa-copy</v-icon>
                    </v-btn>
                  </div>

                  <div class="caption d-flex align-center">
                    <v-icon x-small class="mr-1 grey--text"
                      >fal fa-phone</v-icon
                    >
                    <a
                      v-if="item.phone_no"
                      :href="'tel:' + item.phone_no"
                      class="text-decoration-none primary--text"
                      @click.stop
                    >
                      {{ item.phone_no }}
                    </a>
                    <span v-else class="grey--text">-</span>
                    <v-btn
                      v-if="item.phone_no"
                      icon
                      x-small
                      class="ml-1 contact-mini-copy"
                      @click.stop="copyText(item.phone_no, 'Phone')"
                      title="Copy Phone"
                    >
                      <v-icon x-small>fal fa-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- Col 4: Status Indicator -->
              <div class="staff-col-status mr-3 my-1 text-center">
                <div class="caption grey--text mb-1">{{ $t("status") }}</div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      v-on="on"
                      x-small
                      label
                      class="font-weight-medium status-chip"
                      :class="{
                        'status-chip--clickable': user.id !== item.id,
                        'status-chip--active': item.is_active === 1,
                        'status-chip--inactive': item.is_active !== 1,
                      }"
                      @click.stop="user.id !== item.id && menuAction(2, item)"
                    >
                      <v-icon x-small left class="mr-1">fas fa-circle</v-icon>
                      {{
                        item.is_active === 1
                          ? $t("active") || "Active"
                          : $t("inactive") || "Inactive"
                      }}
                    </v-chip>
                  </template>
                  <span>
                    {{
                      user.id === item.id
                        ? "Your current staff account"
                        : item.is_active === 1
                        ? "Click to deactivate"
                        : "Click to activate"
                    }}
                  </span>
                </v-tooltip>
              </div>

              <!-- Col 5: Actions Bar -->
              <div class="staff-col-actions my-1" @click.stop>
                <div class="d-flex align-center justify-end" style="gap: 6px">
                  <!-- View Details -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        class="action-btn"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="viewItem(item)"
                      >
                        <v-icon small>fal fa-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("details") || "View Details" }}</span>
                  </v-tooltip>

                  <!-- Edit Profile -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        class="action-btn action-btn-edit"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="menuAction(3, item)"
                      >
                        <v-icon small>fal fa-edit</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("edit_profile") }}</span>
                  </v-tooltip>

                  <!-- Change Password -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        class="action-btn action-btn-password"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="menuAction(0, item)"
                      >
                        <v-icon small>fal fa-key</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("change_password") }}</span>
                  </v-tooltip>

                  <!-- Permissions (hidden for self) -->
                  <v-tooltip bottom v-if="user.id !== item.id">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        class="action-btn action-btn-permission"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="menuAction(1, item)"
                      >
                        <v-icon small>fal fa-user-shield</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("permission") }}</span>
                  </v-tooltip>

                  <!-- Deactivate / Activate Action Button (hidden for self) -->
                  <v-tooltip bottom v-if="user.id !== item.id">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        class="action-btn"
                        :class="
                          item.is_active === 1
                            ? 'action-btn-deactivate'
                            : 'action-btn-activate'
                        "
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="menuAction(2, item)"
                      >
                        <v-icon small>
                          fal fa-{{
                            item.is_active === 1 ? "user-times" : "user-check"
                          }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>
                      {{
                        item.is_active === 1
                          ? $t("inactive_account") || "Deactivate"
                          : $t("active_account") || "Activate"
                      }}
                    </span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </v-card>

    <!-- 1. STAFF DETAILS MODAL (REDESIGNED) -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      width="560px"
      title="staff_details"
      titleIcon="fal fa-id-card"
    >
      <div slot="activator" class="pa-2" v-if="form">
        <!-- Hero Profile Banner -->
        <div class="user-detail-hero rounded-xl pa-4 mb-4 text-center">
          <v-avatar size="72" color="primary" class="mb-2 user-detail-avatar">
            <app-img
              v-if="form.avatar"
              width="72"
              height="72"
              :img="form.avatar"
            />
            <span v-else class="white--text font-weight-bold text-h5">
              {{ initials(form.first_name, form.last_name) }}
            </span>
          </v-avatar>
          <h3 class="font-weight-bold text-h6 mb-1">
            {{ getFullName(form) }}
          </h3>
          <div
            class="d-flex align-center justify-center flex-wrap"
            style="gap: 8px"
          >
            <v-chip
              v-if="user.id === form.id"
              x-small
              color="primary"
              label
              class="font-weight-bold"
            >
              {{ $t("you") }}
            </v-chip>
            <v-chip
              dark
              x-small
              label
              :color="RoleColor(form.role)"
              class="text-capitalize"
            >
              {{ userRole(form.role) }}
            </v-chip>
            <v-chip
              x-small
              label
              :color="form.is_active === 1 ? 'success' : 'error'"
              text-color="white"
            >
              {{ form.is_active === 1 ? $t("active") : $t("inactive") }}
            </v-chip>
          </div>
        </div>

        <!-- Information Grid -->
        <v-card outlined class="rounded-xl pa-3 mb-4">
          <v-row dense>
            <v-col cols="6" class="py-2 border-bottom">
              <div class="caption grey--text">{{ $t("id") }}</div>
              <div class="font-weight-bold text-body-2">#{{ form.id }}</div>
            </v-col>
            <v-col cols="6" class="py-2 border-bottom">
              <div class="caption grey--text">{{ $t("branch") }}</div>
              <div class="font-weight-bold text-body-2">
                {{ getBranchName(form) }}
              </div>
            </v-col>
            <v-col cols="6" class="py-2 border-bottom">
              <div class="caption grey--text">{{ $t("phone_no") }}</div>
              <div class="font-weight-bold text-body-2 d-flex align-center">
                <span>{{ form.phone_no || "-" }}</span>
                <v-btn
                  v-if="form.phone_no"
                  icon
                  x-small
                  class="ml-1"
                  @click="copyText(form.phone_no, 'Phone')"
                >
                  <v-icon x-small>fal fa-copy</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="6" class="py-2 border-bottom">
              <div class="caption grey--text">{{ $t("email") }}</div>
              <div
                class="font-weight-bold text-body-2 d-flex align-center text-truncate"
              >
                <span class="text-truncate">{{ form.email || "-" }}</span>
                <v-btn
                  v-if="form.email"
                  icon
                  x-small
                  class="ml-1 flex-shrink-0"
                  @click="copyText(form.email, 'Email')"
                >
                  <v-icon x-small>fal fa-copy</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" class="py-2">
              <div class="caption grey--text">{{ $t("created_at") }}</div>
              <div class="font-weight-bold text-body-2">
                {{ form.created_at | moment }}
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Quick Modal Actions -->
        <div
          class="d-flex align-center justify-space-between flex-wrap"
          style="gap: 8px"
        >
          <v-btn outlined small class="rounded-lg" @click="dialog = false">
            {{ $t("close") }}
          </v-btn>

          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              depressed
              small
              color="primary"
              class="white--text rounded-lg"
              @click="editFromDetail"
            >
              <v-icon left x-small>fal fa-edit</v-icon>
              {{ $t("edit_profile") }}
            </v-btn>
            <v-btn
              outlined
              small
              color="info"
              class="rounded-lg"
              @click="passwordFromDetail"
            >
              <v-icon left x-small>fal fa-key</v-icon>
              {{ $t("change_password") }}
            </v-btn>
            <v-btn
              v-if="user.id !== form.id"
              outlined
              small
              color="purple"
              class="rounded-lg"
              @click="permissionFromDetail"
            >
              <v-icon left x-small>fal fa-user-shield</v-icon>
              {{ $t("permission") }}
            </v-btn>
          </div>
        </div>
      </div>
    </app-custom-dialog>

    <!-- 2. ADD / EDIT STAFF DIALOG (REDESIGNED) -->
    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      width="640px"
      :title="titleStatus"
      :titleIcon="update ? 'fal fa-user-edit' : 'fal fa-user-plus'"
      @submit="update ? updateProfile() : submitForm()"
      :submitText="update ? 'update' : 'add_staff'"
    >
      <div slot="activator" class="pa-1">
        <!-- Section 1: Personal Information -->
        <div class="form-section-title mb-2 d-flex align-center">
          <v-icon small color="primary" class="mr-2">fal fa-id-badge</v-icon>
          <span class="font-weight-bold subtitle-2">{{
            $t("personal_info")
          }}</span>
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <TTextField
              @input="$v.form.first_name.$touch()"
              @blur="$v.form.first_name.$touch()"
              :errorMessages="first_nameError"
              label="first_name"
              v-model="form.first_name"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <TTextField label="last_name" v-model="form.last_name" />
          </v-col>
        </v-row>

        <TTextField
          label="phone_no"
          class="mt-3"
          v-model="form.phone_no"
          placeholder="205xxxxxxx"
          :errorMessages="phone_numberError"
          @input="$v.form.phone_no.$touch()"
          @blur="$v.form.phone_no.$touch()"
          :maxlength="10"
        />

        <v-divider class="my-4"></v-divider>

        <!-- Section 2: Role & Branch Assignment -->
        <div class="form-section-title mb-2 d-flex align-center">
          <v-icon small color="primary" class="mr-2">fal fa-sitemap</v-icon>
          <span class="font-weight-bold subtitle-2">{{
            $t("role_and_branch")
          }}</span>
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <TextSelect
              label="role"
              v-model="form.role"
              :errorMessages="roleErrors"
              @input="$v.form.role.$touch()"
              @blur="$v.form.role.$touch()"
              itemValue="id"
              text="name"
              :items="status"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <TextSelect
              label="branch"
              v-model="form.branch_id"
              :disabled="branches.length === 1 && !update"
              :errorMessages="branch_idErrors"
              @input="$v.form.branch_id.$touch()"
              @blur="$v.form.branch_id.$touch()"
              :items="branches"
              itemValue="id"
              text="name"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Section 3: Credentials & Access -->
        <div class="form-section-title mb-2 d-flex align-center">
          <v-icon small color="primary" class="mr-2">fal fa-lock-alt</v-icon>
          <span class="font-weight-bold subtitle-2">{{
            $t("credentials")
          }}</span>
        </div>

        <TTextField
          :errorMessages="emailError"
          label="email"
          v-model="form.email"
          placeholder="staff@example.com"
          @input="$v.form.email.$touch()"
          @blur="$v.form.email.$touch()"
        />

        <!-- In update mode, show note that password is optional -->
        <div
          v-if="update"
          class="caption grey--text mt-2 pa-2 rounded-lg grey lighten-4"
        >
          <v-icon x-small color="info" class="mr-1">fal fa-info-circle</v-icon>
          {{ $t("leave_blank_keep_password") }}
        </div>

        <TPassword
          class="mt-3"
          v-model="form.password"
          @input="$v.form.password.$touch()"
          @blur="$v.form.password.$touch()"
          :errorMessages="passwordErrors"
          label="password"
          showKey="showPassword"
          @showPassword="showPassword = !showPassword"
          :type="showPassword"
        >
          <template v-slot:append>
            <v-btn
              x-small
              text
              color="primary"
              class="px-2 font-weight-bold"
              @click.stop="generatePassword"
            >
              <v-icon x-small left>fal fa-magic</v-icon>
              {{ $t("generate") }}
            </v-btn>
          </template>
        </TPassword>

        <TPassword
          class="mt-3"
          v-model="form.confirm_password"
          @input="$v.form.confirm_password.$touch()"
          @blur="$v.form.confirm_password.$touch()"
          :errorMessages="confirmPasswordErrors"
          @showCP="showCP = !showCP"
          label="confirm_password"
          showKey="showCP"
          :type="showCP"
        />
      </div>
    </app-custom-dialog>

    <!-- 3. CHANGE PASSWORD DIALOG -->
    <app-custom-dialog
      v-model="changePassword"
      :footer="true"
      width="460px"
      title="change_password"
      titleIcon="fal fa-key"
      @submit="submitFormChangePassword"
    >
      <div slot="activator" class="pa-1">
        <p class="caption grey--text mb-3">
          {{ $t("change_staff_password_hint") }}
        </p>

        <TPassword
          class="mt-2"
          v-model="formChange.password"
          @input="$v.formChange.password.$touch()"
          @blur="$v.formChange.password.$touch()"
          :errorMessages="passwordErrorsC"
          label="password"
          showKey="showPassword"
          @showPassword="showPasswordC = !showPasswordC"
          :type="showPasswordC"
        />

        <TPassword
          class="mt-3"
          v-model="formChange.confirm_password"
          @input="$v.formChange.confirm_password.$touch()"
          @blur="$v.formChange.confirm_password.$touch()"
          :errorMessages="confirmPasswordErrorsC"
          @showCP="showCPC = !showCPC"
          label="confirm_password"
          showKey="showCP"
          :type="showCPC"
        />
      </div>
    </app-custom-dialog>

    <!-- 4. ROLE & PERMISSION MATRIX DIALOG (REDESIGNED) -->
    <app-custom-dialog
      v-model="dialogRole"
      :footer="true"
      title="permission"
      titleIcon="fal fa-user-shield"
      width="820px"
      @submit="submitPermission"
    >
      <div slot="activator" class="pa-1">
        <!-- User Context Header in Modal -->
        <div
          class="d-flex align-center justify-space-between flex-wrap rounded-lg pa-3 mb-3 permission-user-header"
        >
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">fal fa-user-circle</v-icon>
            <div>
              <div class="font-weight-bold text-subtitle-2">
                {{ getFullName(form) }}
              </div>
              <div class="caption grey--text">
                {{ userRole(form.role) }} · {{ getBranchName(form) }}
              </div>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 6px">
            <v-btn
              x-small
              outlined
              color="primary"
              class="rounded-pill"
              @click="selectAllPermissions"
            >
              {{ $t("select_all") }}
            </v-btn>
            <v-btn
              x-small
              outlined
              color="grey"
              class="rounded-pill"
              @click="deselectAllPermissions"
            >
              {{ $t("deselect_all") }}
            </v-btn>
          </div>
        </div>

        <!-- Filter tabs & Search within Permissions -->
        <div
          class="d-flex align-center justify-space-between flex-wrap mb-3"
          style="gap: 8px"
        >
          <div class="br-tabs">
            <div
              v-for="item in roleTypeList"
              :key="item.value"
              class="br-tab"
              :class="{ 'br-tab--active': roleType === item.value }"
              @click="roleType = item.value; roleAction(item.value)"
            >
              {{ $t(item.text) }}
            </div>
          </div>

          <div style="max-width: 240px; width: 100%">
            <v-text-field
              v-model="permissionSearch"
              dense
              outlined
              hide-details
              small
              :placeholder="$t('filter_permissions')"
              prepend-inner-icon="fal fa-search"
              clearable
              class="rounded-lg"
            ></v-text-field>
          </div>
        </div>

        <!-- Checkbox Hierarchy Grid -->
        <div class="permissions-scroll-container">
          <v-row dense>
            <template v-for="item in searchedFilteredMenus">
              <v-col :key="item.id" cols="12" sm="6" class="mb-2">
                <v-card
                  outlined
                  class="pa-3 rounded-lg permission-group-card h-100"
                >
                  <!-- Parent Item -->
                  <div class="d-flex align-center justify-space-between">
                    <v-checkbox
                      dense
                      hide-details
                      class="ma-0 pa-0 font-weight-bold parent-permission-check"
                      v-model="selected"
                      :label="$t(item.code) || item.name || item.code"
                      :value="item.id"
                      @change="handleParentToggle(item)"
                    />
                    <v-icon x-small :color="item.icon ? 'primary' : 'grey'">
                      {{ item.icon || "fal fa-cog" }}
                    </v-icon>
                  </div>

                  <!-- Sub Menus -->
                  <div
                    v-if="item.menu && item.menu.length"
                    class="pl-4 mt-2 border-left sub-menu-list"
                  >
                    <v-checkbox
                      v-for="sub in item.menu"
                      :key="sub.id"
                      dense
                      hide-details
                      class="ma-0 pa-0 my-1 caption"
                      v-model="selected"
                      :label="$t(sub.code) || sub.name || sub.code"
                      :value="sub.id"
                    />
                  </div>
                </v-card>
              </v-col>
            </template>
          </v-row>
        </div>
      </div>
    </app-custom-dialog>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import TTextField from "@/components/global/TextField";
import { AddUserStore } from "@/validations/shop";
import TextSelect from "@/components/global/TextSelect";
import TPassword from "@/components/global/TextPassword";
import { myMixin } from "@/mixin/main";
import { success, confirm } from "@/controllers/alert";
import Help from "@/utils/helper";

export default {
  name: "ManageUsersPage",
  mixins: [AddUserStore, myMixin],
  data: () => ({
    roleTypeList: [
      {
        text: "all",
        value: 0,
      },
      {
        text: "menu",
        value: 2,
      },
      {
        text: "role",
        value: 1,
      },
    ],
    roleType: 0,
    update: false,
    dialogRole: false,
    dialogAdd: false,
    dialog: false,
    showPassword: false,
    showCP: false,
    showPasswordC: false,
    showCPC: false,
    changePassword: false,
    menus: [],
    selected: [],
    id: null,
    formChange: {},
    overlay: false,
    branches: [],
    users: [],
    summaries: [],
    loadingSummary: true,
    loadingUsers: false,
    searchQuery: "",
    selectedStatusFilter: "all",
    selectedBranchFilter: "all",
    permissionSearch: "",
    form: {
      id: 0,
      first_name: "",
      last_name: "",
      phone_no: "",
      role: null,
      branch_id: null,
      email: "",
      password: "",
      confirm_password: "",
    },
  }),

  async created() {
    this.loadingUsers = true;
    try {
      await Promise.all([
        this.fetchItem(),
        this.fetchBranch(),
        this.fetchSummary(),
      ]);
    } finally {
      this.loadingUsers = false;
    }
  },

  components: {
    TTextField,
    TextSelect,
    TPassword,
  },

  watch: {
    selectedStatusFilter() {
      this.fetchItem();
    },
    selectedBranchFilter() {
      this.fetchItem();
    },
  },

  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        label: this.getKpiTitle(item.title),
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    titleStatus() {
      return this.update ? "edit_staff_profile" : "add_staff";
    },
    status() {
      return [
        {
          id: 3,
          name: this.$t("manager"),
        },
        {
          id: 4,
          name: this.$t("assistant"),
        },
        {
          id: 5,
          name: this.$t("staff"),
        },
      ];
    },
    userList() {
      if (this.users && Array.isArray(this.users.data)) {
        return this.users.data;
      }
      if (Array.isArray(this.users)) {
        return this.users;
      }
      return [];
    },
    totalStaffCount() {
      const s = (this.summaries || []).find((item) => item.title === "total_users");
      if (s) return s.total;
      return this.userList.length;
    },
    activeCount() {
      const s = (this.summaries || []).find((item) => item.title === "active_users");
      if (s) return s.total;
      return this.userList.filter((u) => u.is_active === 1).length;
    },
    inactiveCount() {
      const s = (this.summaries || []).find((item) => item.title === "inactive_users");
      if (s) return s.total;
      return this.userList.filter((u) => u.is_active === 0).length;
    },
    branchFilterOptions() {
      const allOption = [{ id: "all", name: this.$t("all") || "All Branches" }];
      if (Array.isArray(this.branches)) {
        return allOption.concat(this.branches);
      }
      return allOption;
    },
    isAnyFilterActive() {
      return (
        !!this.searchQuery ||
        this.selectedStatusFilter !== "all" ||
        this.selectedBranchFilter !== "all"
      );
    },
    filteredUsers() {
      let list = this.userList;
      if (this.selectedStatusFilter !== "all") {
        const active = Number(this.selectedStatusFilter);
        list = list.filter((u) => u.is_active === active);
      }
      if (this.selectedBranchFilter !== "all") {
        const branchId = Number(this.selectedBranchFilter);
        list = list.filter((u) => {
          const bId =
            u.branch_id && typeof u.branch_id === "object"
              ? u.branch_id.id
              : u.branch_id;
          return Number(bId) === branchId;
        });
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((u) => {
          const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
          const phone = String(u.phone_no || "").toLowerCase();
          const email = String(u.email || "").toLowerCase();
          return name.includes(q) || phone.includes(q) || email.includes(q);
        });
      }
      return list;
    },
    filteredMenus() {
      if (!this.menus || this.menus.length === 0) {
        return [];
      }

      switch (this.roleType) {
        case 0: // "all"
          return this.menus;
        case 1: // "role" - items without sub-menus
          return this.menus.filter((item) => item.icon === null);
        case 2: // "menu" - items with icons
          return this.menus.filter((item) => item.icon !== null);
        default:
          return this.menus;
      }
    },
    searchedFilteredMenus() {
      let menus = this.filteredMenus;
      if (!this.permissionSearch || !this.permissionSearch.trim()) {
        return menus;
      }
      const q = this.permissionSearch.trim().toLowerCase();
      return menus.filter((item) => {
        const itemLabel = (
          this.$t(item.code) ||
          item.name ||
          item.code ||
          ""
        ).toLowerCase();
        const subMatch = (item.menu || []).some((sub) =>
          (this.$t(sub.code) || sub.name || sub.code || "")
            .toLowerCase()
            .includes(q)
        );
        return itemLabel.includes(q) || subMatch;
      });
    },
  },

  methods: {
    onSearchInput() {
      // Filter is computed reactively via filteredUsers
    },

    onClearSearch() {
      this.searchQuery = "";
    },

    getKpiTitle(title) {
      if (title === "total_users") return this.$t("total_staff");
      if (title === "active_users") return this.$t("active_staff");
      if (title === "inactive_users") return this.$t("inactive_staff");
      return this.$t(title);
    },

    getFullName(item) {
      if (!item) return "-";
      const first = (item.first_name || "").trim();
      const last = (item.last_name || "").trim();
      return `${first} ${last}`.trim() || "-";
    },

    getBranchName(item) {
      if (!item) return "-";
      if (item.branch_id && typeof item.branch_id === "object") {
        return item.branch_id.name || "-";
      }
      if (item.branch_id && Array.isArray(this.branches)) {
        const found = this.branches.find((b) => b.id == item.branch_id);
        if (found) return found.name;
      }
      return this.$t("all") || "-";
    },

    getRoleCount(roleId) {
      return this.userList.filter((u) => u.role === roleId).length;
    },

    initials(first, last) {
      const f = (first || "").trim();
      const l = (last || "").trim();
      if (!f && !l) return "?";
      if (f && l) {
        return (f.charAt(0) + l.charAt(0)).toUpperCase();
      }
      return f.substring(0, 2).toUpperCase();
    },

    copyText(text, label = "") {
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(String(text))
          .then(() => {
            this.$toast.success(
              label ? `${label} copied` : this.$t("copied") || "Copied!"
            );
          })
          .catch(() => {});
      }
    },

    resetFilters() {
      this.searchQuery = "";
      this.selectedStatusFilter = "all";
      this.selectedBranchFilter = "all";
    },

    isKpiActive(kpiTitle) {
      if (kpiTitle === "active_users" && this.selectedStatusFilter === 1)
        return true;
      if (kpiTitle === "inactive_users" && this.selectedStatusFilter === 0)
        return true;
      if (
        kpiTitle === "total_users" &&
        this.selectedStatusFilter === "all" &&
        !this.isAnyFilterActive
      )
        return true;
      return false;
    },

    handleKpiClick(kpiTitle) {
      if (kpiTitle === "active_users") {
        this.selectedStatusFilter = this.selectedStatusFilter === 1 ? "all" : 1;
      } else if (kpiTitle === "inactive_users") {
        this.selectedStatusFilter = this.selectedStatusFilter === 0 ? "all" : 0;
      } else if (kpiTitle === "total_users") {
        this.resetFilters();
      }
    },

    async refreshAll() {
      this.loadingUsers = true;
      try {
        await Promise.all([
          this.fetchItem(),
          this.fetchBranch(),
          this.fetchSummary(),
        ]);
        this.$toast.success(this.$t("refresh_success"));
      } finally {
        this.loadingUsers = false;
      }
    },

    async fetchBranch() {
      try {
        const { data } = await this.$http.get("branches");
        this.branches = data.data;
        if (
          !this.update &&
          Array.isArray(this.branches) &&
          this.branches.length > 0
        ) {
          this.form.branch_id = this.branches[0];
        }
      } catch (e) {
        console.error("fetchBranch error", e);
      }
    },

    async fetchItem() {
      try {
        const { data } = await this.$http.get("get_users");
        this.users = data.data;
      } catch (e) {
        console.error("fetchItem error", e);
      }
    },

    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const { data } = await this.$http.get("get_summary_users");
        if (data && data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log("error fetching users summary", error);
      } finally {
        this.loadingSummary = false;
      }
    },

    async newItem() {
      await Help.clearForm(this.form, this);
      this.update = false;
      if (Array.isArray(this.branches) && this.branches.length > 0) {
        this.form.branch_id = this.branches[0];
      }
      this.dialogAdd = true;
    },

    viewItem(item) {
      this.form = { ...item };
      this.dialog = true;
    },

    editFromDetail() {
      this.dialog = false;
      this.editProfile(this.form);
    },

    passwordFromDetail() {
      this.dialog = false;
      this.changePass(this.form);
    },

    permissionFromDetail() {
      this.dialog = false;
      this.roleSetting(this.form);
    },

    async changePass(item) {
      this.id = item.id;
      this.formChange = { password: "", confirm_password: "" };
      this.changePassword = true;
    },

    async roleSetting(item) {
      this.overlay = true;
      this.selected = [];
      this.menus = [];
      this.roleType = 0;
      this.permissionSearch = "";
      try {
        const { data } = await this.$http.get(`get_user_role/${item.id}`);
        data.data.forEach((menuItem) => {
          if (menuItem.checked) {
            this.selected.push(menuItem.id);
          }
          if (menuItem.menu && menuItem.menu.length) {
            menuItem.menu.forEach((sub) => {
              if (sub.checked) {
                this.selected.push(sub.id);
              }
            });
          }
        });
        this.form = { ...item };
        this.menus = data.data;
        this.dialogRole = true;
      } catch (e) {
        console.error("roleSetting error", e);
      } finally {
        this.overlay = false;
      }
    },

    selectAllPermissions() {
      const allIds = [];
      this.menus.forEach((m) => {
        allIds.push(m.id);
        if (m.menu && m.menu.length) {
          m.menu.forEach((sub) => allIds.push(sub.id));
        }
      });
      this.selected = Array.from(new Set(allIds));
    },

    deselectAllPermissions() {
      this.selected = [];
    },

    handleParentToggle(parentItem) {
      const isParentChecked = this.selected.includes(parentItem.id);
      if (parentItem.menu && parentItem.menu.length) {
        parentItem.menu.forEach((sub) => {
          const subIdx = this.selected.indexOf(sub.id);
          if (isParentChecked && subIdx === -1) {
            this.selected.push(sub.id);
          } else if (!isParentChecked && subIdx !== -1) {
            this.selected.splice(subIdx, 1);
          }
        });
      }
    },

    async userRoleAction(val) {
      const status =
        val.is_active === 0 ? "active_account" : "inactive_account";
      const { isConfirmed } = await confirm({
        props: this,
        text: status,
      });
      if (isConfirmed) {
        const { data } = await this.$http.post(
          `store_set_action/${val.id}`,
          val
        );
        if (data.status) {
          await this.fetchItem();
          await this.fetchSummary();
          await success(this, data.message);
        }
      }
    },

    async menuAction(val, item) {
      switch (val) {
        case 0:
          await this.changePass(item);
          break;
        case 1:
          await this.roleSetting(item);
          break;
        case 2:
          await this.userRoleAction(item);
          break;
        case 3:
          await this.editProfile(item);
          break;
        default:
          break;
      }
    },

    async editProfile(item) {
      this.form = { ...item };
      // Map branch object if available
      if (item.branch_id && typeof item.branch_id === "object") {
        this.form.branch_id = item.branch_id;
      } else if (item.branch_id && Array.isArray(this.branches)) {
        this.form.branch_id =
          this.branches.find((b) => b.id == item.branch_id) || item.branch_id;
      }
      // Map role object if available
      const foundRole = this.status.find((r) => r.id === item.role);
      if (foundRole) {
        this.form.role = foundRole;
      }
      this.form.password = "";
      this.form.confirm_password = "";
      this.update = true;
      this.dialogAdd = true;
    },

    generatePassword() {
      const first = (this.form.first_name || "").trim();
      const last = (this.form.last_name || "").trim();

      const firstInitial = first ? first.charAt(0).toUpperCase() : "U";
      const lastInitial = last ? last.charAt(0).toLowerCase() : "u";

      const d = new Date();
      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyymmdd = `${yyyy}${mm}${dd}`;

      const pwd = `${firstInitial}${lastInitial}@${yyyymmdd}`;
      this.form.password = pwd;
      this.form.confirm_password = pwd;

      this.$v.form.password.$touch();
      this.$v.form.confirm_password.$touch();
      this.copyText(pwd, "Generated Password");
    },

    async submitFormChangePassword() {
      try {
        this.overlay = true;
        this.formChange.userId = this.id;
        const { data } = await this.$http.post(
          `store_change_password`,
          this.formChange
        );
        if (data.status) {
          this.overlay = false;
          this.changePassword = false;
          await success(this, data.message);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        this.overlay = false;
      }
    },

    async submitForm() {
      try {
        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          this.overlay = true;

          const body = {
            ...this.form,
            branch_id: Help.getId(this.form.branch_id),
            role: Help.getId(this.form.role),
          };
          const { data } = await this.$http.post("store_create_user", body);
          if (data.status) {
            this.overlay = false;
            this.dialogAdd = false;
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await this.fetchSummary();
            await success(this, data.message);
          }
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        this.overlay = false;
      }
    },

    async updateProfile() {
      try {
        const body = {
          id: this.form.id,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          phone_no: this.form.phone_no,
          role: Help.getId(this.form.role),
          branch_id: Help.getId(this.form.branch_id),
          email: this.form.email,
        };
        if (this.form.password && this.form.confirm_password) {
          body.password = this.form.password;
          body.confirm_password = this.form.confirm_password;
        }
        const { data } = await this.$http.post("store_update_user", body);
        if (data.status) {
          this.overlay = false;
          this.dialogAdd = false;
          await this.fetchItem();
          await this.fetchSummary();
          await success(this, data.message);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        this.overlay = false;
      }
    },

    async submitPermission() {
      try {
        const form = {
          userId: this.form.id,
          menus: Help.menuCheck(this.menus, this.selected),
        };
        const { data } = await this.$http.post("menu_assigned", form);
        if (data.status) {
          this.selected = [];
          this.menus = [];
          this.dialogRole = false;
          await Help.clearForm(this.form, this);
          await this.fetchItem();
          await success(this, data.message);
        }
      } catch (error) {
        console.log("error", error);
      }
    },

    roleAction(val) {
      this.roleType = val;
    },
  },
};
</script>

<style scoped>
.ps-manage-users-page {
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}



/* Main Card */
.main-users-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  background: #ffffff;
  box-shadow: none !important;
}

.theme--dark .main-users-card {
  border-color: #334155 !important;
  background: #1e293b;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

.theme--dark .border-top {
  border-top: 1px solid #334155;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.theme--dark .border-bottom {
  border-bottom: 1px solid #334155;
}

/* Staff Horizontal List Card (Styled like .ps-prod-card on sale page: clean border, 16px radius, zero shadow) */
.staff-row-card,
.user-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.12s ease;
  position: relative;
  overflow: hidden;
}

.theme--dark .staff-row-card,
.theme--dark .user-card {
  border-color: #334155 !important;
  background: #1e293b !important;
}

.staff-row-card:hover,
.user-card:hover {
  border-color: #5b39c9 !important;
  box-shadow: none !important;
  transform: translateY(-1px);
}

.theme--dark .staff-row-card:hover,
.theme--dark .user-card:hover {
  border-color: #8b6ff0 !important;
}

.staff-row-card:active,
.user-card:active {
  transform: scale(0.995);
}

.staff-row-card--self,
.user-card--self {
  border-left: 4px solid #5b39c9 !important;
}

.theme--dark .staff-row-card--self,
.theme--dark .user-card--self {
  border-left-color: #8b6ff0 !important;
}

.staff-row-card--inactive,
.user-card--inactive {
  opacity: 0.82;
  background-color: #fcfcfd !important;
}

.theme--dark .staff-row-card--inactive,
.theme--dark .user-card--inactive {
  background-color: #172030 !important;
}

/* Staff Card Internal Elements */
.staff-name-link,
.user-name-text {
  color: #0f172a;
  transition: color 0.15s ease;
}

.theme--dark .staff-name-link,
.theme--dark .user-name-text {
  color: #f8fafc;
}

.staff-row-card:hover .staff-name-link,
.user-card:hover .user-name-text,
.staff-name-link:hover,
.user-name-text:hover {
  color: #5b39c9;
}

.theme--dark .staff-row-card:hover .staff-name-link,
.theme--dark .user-card:hover .user-name-text {
  color: #c4b5fd;
}

.staff-avatar,
.user-avatar {
  border: 2px solid #ffffff;
}

.theme--dark .staff-avatar,
.theme--dark .user-avatar {
  border-color: #1e293b;
}

.staff-col-identity {
  min-width: 220px;
}

.staff-col-branch {
  min-width: 120px;
}

.staff-col-contact {
  min-width: 170px;
}

.staff-col-status {
  min-width: 85px;
}

.staff-col-actions {
  min-width: 180px;
}

.user-branch-chip {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.theme--dark .user-branch-chip {
  background: #0f172a !important;
  border-color: #334155 !important;
}

.self-badge {
  letter-spacing: 0.5px;
  border-radius: 4px;
}

.user-id-pill {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.user-id-pill:hover {
  background: #e2e8f0;
  color: #334155;
}

.theme--dark .user-id-pill {
  background: #334155;
  color: #cbd5e1;
}

.user-branch-tag {
  font-size: 11px;
  font-weight: 500;
  color: #5b39c9;
  background: #f3f0fc;
  padding: 1px 7px;
  border-radius: 4px;
  max-width: 140px;
}

.theme--dark .user-branch-tag {
  background: rgba(91, 57, 201, 0.2);
  color: #c4b5fd;
}

/* Status Chips */
.status-chip {
  border-radius: 6px !important;
  font-size: 11px;
  letter-spacing: 0.2px;
}

.status-chip--clickable {
  cursor: pointer;
  transition: transform 0.15s ease;
}

.status-chip--clickable:hover {
  transform: scale(1.05);
}

.status-chip--active {
  background-color: #ecfdf5 !important;
  color: #059669 !important;
  border: 1px solid #a7f3d0 !important;
}

.theme--dark .status-chip--active {
  background-color: rgba(5, 150, 105, 0.2) !important;
  border-color: rgba(5, 150, 105, 0.4) !important;
}

.status-chip--inactive {
  background-color: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

.theme--dark .status-chip--inactive {
  background-color: rgba(220, 38, 38, 0.2) !important;
  border-color: rgba(220, 38, 38, 0.4) !important;
}

/* Role Pill */
.role-pill {
  border-radius: 6px !important;
  letter-spacing: 0.3px;
}

/* Contact Details Box */
.contact-details-box {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}

.theme--dark .contact-details-box {
  background: #0f172a;
  border-color: #1e293b;
}

.contact-row {
  line-height: 1.4;
}

.contact-copy-btn {
  opacity: 0.6;
  transition: opacity 0.15s ease;
}

.contact-copy-btn:hover {
  opacity: 1;
}

/* Action Buttons */
.action-btn {
  transition: all 0.18s ease-in-out;
  border-radius: 8px !important;
}

.action-btn-edit {
  color: #10b981 !important;
}

.action-btn-edit:hover {
  background-color: rgba(16, 185, 129, 0.12) !important;
  transform: scale(1.1);
}

.action-btn-password {
  color: #3b82f6 !important;
}

.action-btn-password:hover {
  background-color: rgba(59, 130, 246, 0.12) !important;
  transform: scale(1.1);
}

.action-btn-permission {
  color: #8b5cf6 !important;
}

.action-btn-permission:hover {
  background-color: rgba(139, 92, 246, 0.12) !important;
  transform: scale(1.1);
}

.action-btn-activate {
  color: #10b981 !important;
}

.action-btn-activate:hover {
  background-color: rgba(16, 185, 129, 0.12) !important;
  transform: scale(1.1);
}

.action-btn-deactivate {
  color: #ef4444 !important;
}

.action-btn-deactivate:hover {
  background-color: rgba(239, 68, 68, 0.12) !important;
  transform: scale(1.1);
}

.btn-primary-brand {
  background-color: #5b39c9 !important;
  color: #ffffff !important;
  letter-spacing: 0.3px;
  box-shadow: none !important;
}

.btn-primary-brand:hover {
  background-color: #4f2fb9 !important;
}

/* Filter Toolbar */
.filter-toolbar {
  gap: 12px;
}

.filter-chip {
  font-weight: 500;
  border-color: #cbd5e1;
}

/* Empty State */
.empty-state-container {
  padding: 40px 16px;
}

.empty-state-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f3f0fc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .empty-state-icon-wrap {
  background: rgba(91, 57, 201, 0.2);
}

.max-w-400 {
  max-width: 400px;
}

/* Detail Hero */
.user-detail-hero {
  background: linear-gradient(135deg, #f3f0fc 0%, #ede9fe 100%);
  border: 1px solid #e0e7ff;
}

.theme--dark .user-detail-hero {
  background: linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%);
  border-color: #4338ca;
}

.user-detail-avatar {
  border: 3px solid #ffffff;
}

/* Permission Modal */
.permission-user-header {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.theme--dark .permission-user-header {
  background: #1e293b;
  border-color: #334155;
}

.permissions-scroll-container {
  max-height: 440px;
  overflow-y: auto;
  padding-right: 4px;
}

.permission-group-card {
  transition: border-color 0.15s ease;
  border-color: #e2e8f0;
}

.permission-group-card:hover {
  border-color: #5b39c9;
}

.theme--dark .permission-group-card {
  border-color: #334155;
}

.sub-menu-list {
  border-left: 2px solid #e2e8f0;
}

.theme--dark .sub-menu-list {
  border-left-color: #334155;
}
</style>
